import React, { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react';
import { Box, Button, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import AddIcon from '@mui/icons-material/Add';
import { useSnackbar } from 'notistack';
import qs from 'qs';

import CustomTable from 'components/common/customTableV2';
import Loading from 'components/common/loading';
import PageHeader from 'components/common/pageHeader';
import M from 'messages';
import { routes } from 'config';
import { replacePath, getMessage } from 'helpers/helper';
import CampaignsService from 'services/campaignsService';
import campaignsStore from 'store/campaignsStore';
import { adaptCampaignsTableData } from 'helpers/adapter';
import tableOptions from './config/config';
import CustomTableToolbar from './toolbar';
import domainData from 'store/domainStore';
import { isSuperAdmin } from 'helpers/usersRolesHelper';
import classes from './styles';

const Campaigns = observer(() => {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const pageParams = qs.parse(location.search, { ignoreQueryPrefix: true });

  const [loading, setLoading] = useState(true);
  const [campaignsData, setCampaignsData] = useState({});

  const currentUser = domainData.getCurrentUser();
  const isUserSuperAdmin = isSuperAdmin(currentUser);
  const actingSpId = domainData.getCurrentSP();
  const [countToSendCampaign, setCountToSendCampaign] = useState(0);
  const [countToSendCampaignLoading, setCountToSendCampaignLoading] = useState(false);

  useEffect(() => {
    const getCountToSendCampaign = async () => {
      setCountToSendCampaignLoading(true);
      const res = await CampaignsService.getCreateAbility({ serviceProviderId: actingSpId });
      setCountToSendCampaign({
        countToSendCampaign: res.data.countToSendCampaign,
        campaignMonthlyCount: res.data.campaignMonthlyCount,
        sentCampaignsCount: res.data.sentCampaignsCount,
        planException: res.data.planException,
      });
      setCountToSendCampaignLoading(false);
    };
    if (!isUserSuperAdmin) {
      getCountToSendCampaign();
    }
  }, [isUserSuperAdmin, actingSpId]);

  const setFilter = filter => {
    replacePath(navigate, filter);
    campaignsStore.setFilter(filter);
  };

  const queryParams = campaignsStore.getCurrentFilterParams(pageParams);

  const getCampaigns = useCallback(
    async params => {
      try {
        setFilter(params);
        const response = await CampaignsService.getCampaigns(params);
        if (response) {
          setCampaignsData({
            count: response.data?.count || 0,
            data: adaptCampaignsTableData(response.data?.data || []),
          });
          setLoading(false);
        }
      } catch (err) {
        setLoading(false);
        enqueueSnackbar(getMessage(err?.response?.data, 'error'), { variant: 'error' });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enqueueSnackbar],
  );

  useEffect(() => {
    getCampaigns(queryParams);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCloneAction = async row => {
    try {
      await CampaignsService.duplicate(row.id);
      getCampaigns(queryParams);
    } catch (error) {
      enqueueSnackbar(getMessage(error?.response?.data, 'error'), { variant: 'error' });
    }
  };

  const handleRemoveAction = async rowId => {
    try {
      await CampaignsService.remove(rowId);
      getCampaigns(queryParams);
    } catch (error) {
      enqueueSnackbar(getMessage(error?.response?.data, 'error'), { variant: 'error' });
    }
  };

  const handleCreateCampaigns = () => {
    navigate(routes.campaignsCreate.path);
  };

  const { searchFields } = tableOptions.campaigns;
  const onSearchCallback = value => {
    let oldValue = queryParams.params?.search?.value;
    if (oldValue === undefined) {
      oldValue = '';
    }
    if (searchFields && oldValue !== value) {
      const newFilter = {
        params: {
          ...queryParams.params,
          search: { ...queryParams.params.search, value, fields: searchFields },
          skip: 0,
        },
      };
      if (!value) {
        delete newFilter.params.search;
      }
      setFilter(newFilter);
      getCampaigns(newFilter);
    }
  };

  const toolbarView = <CustomTableToolbar onSearchCallback={onSearchCallback} filteredParams={queryParams} />;

  if (loading) return <Loading />;

  return (
    <>
      <Box sx={classes.root}>
        <PageHeader
          generalPageTitle={M.get('pages.campaigns')}
          element={
            <>
              <Grid size={{ xs: 6 }}>
                {/* vertical align the text to the center */}
                {!isUserSuperAdmin &&
                  !countToSendCampaignLoading &&
                  (countToSendCampaign.planException ? (
                    <Typography sx={classes.campaignQuotaText}>
                      <b>Campaign Quota: </b> Unlimited usage.
                    </Typography>
                  ) : (
                    <Box>
                      <Typography sx={classes.campaignQuotaText}>
                        <b>Campaign Quota: </b>
                        {countToSendCampaign.sentCampaignsCount || 0}/{countToSendCampaign.campaignMonthlyCount || 0}{' '}
                        used this month.
                      </Typography>
                      {!countToSendCampaign.campaignMonthlyCount && (
                        <Typography sx={classes.campaignQuotaNoAccessText}>
                          Please &nbsp;
                          <a
                            style={classes.link}
                            href={`${routes.subscriptions.path}?type=standard`}
                            rel="noopener noreferrer">
                            {M.get('subscriptions.upgrade')}
                          </a>
                          &nbsp; your plan to create more campaigns.
                        </Typography>
                      )}
                    </Box>
                  ))}
              </Grid>
              <Grid size={{ xs: 6 }} sx={classes.actionPart}>
                <Button
                  onClick={handleCreateCampaigns}
                  startIcon={<AddIcon sx={classes.primaryIcon} />}
                  sx={classes.primaryIconButton}
                  variant="contained"
                  color="primary">
                  {M.get('pages.createNewCampaign')}
                </Button>
              </Grid>
            </>
          }
          onlyGeneralPageTitle={false}
        />
        <Grid size={{ xs: 12 }}>
          <Box sx={classes.tableRoot}>
            <Grid container sx={classes.container}>
              <Box sx={classes.tabsContainer}>
                <CustomTable
                  tableSources={campaignsData}
                  tableOptions={tableOptions.campaigns}
                  loading={loading}
                  filteredParams={queryParams}
                  setFilteredParams={setFilter}
                  getDataOnChange={getCampaigns}
                  withOpenAction={false}
                  withEditAction
                  withCloneAction
                  handleCloneAction={handleCloneAction}
                  withRemoveAction
                  removeActionCondition={row => row.status !== 'Sent'}
                  handleRemoveAction={handleRemoveAction}
                  toolbarView={toolbarView}
                  withCheckbox={false}
                  stickyHeader
                />
              </Box>
            </Grid>
          </Box>
        </Grid>
      </Box>
    </>
  );
});

export default Campaigns;
